import React, { useState, useEffect } from "react";
import PageHeading from "../ui/PageHeading";
import RocDateInput from "../ui/RocDateInput";
import { isFormerPersonnel, personnelStatusFields, taipeiDateKey } from "../../utils/personnelStatus";
import { subscribePersonnel } from "../../utils/subscribePersonnel";
import {
  doc,
  onSnapshot,
  query,
  collection,
  setDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
  updateDoc,
  orderBy,
  limit,
} from "../../config/dataClient";
import { signOut } from "../../config/authClient";
import { isLocalPreview } from "../../config/previewMode";
import {
  Settings,
  User,
  MapPin,
  Check,
  AlertCircle,
  Edit2,
  X,
  ClipboardList,
  Trash2,
  Eye,
  EyeOff,
  Shield,
  UserPlus,
  DollarSign,
  ChevronDown,
  ChevronUp,
  FileText, 
  Globe, 
  Navigation,
  Briefcase,
  Heart,
  PhoneCall,
  FileBadge,
  Plus,
} from "lucide-react";
import { db, auth } from "../../config/firebase";
import { APP_ID, ROLES } from "../../config/constants";
import { useToast, useConfirm } from "../ui/ToastConfirm";
import EditProfileModal from "./settings/EditProfileModal";
import ReviewModal from "./settings/ReviewModal";
import TestDataPanel from "./settings/TestDataPanel";

export default function SettingsView({ user, userProfile, role }) {
  const [currentIP, setCurrentIP] = useState("偵測中...");
  const [settings, setSettings] = useState({});
  const [currentLocation, setCurrentLocation] = useState(null);
  const [adminList, setAdminList] = useState([]);

  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [today, setToday] = useState(() => taipeiDateKey());
  
  const [saveStatus, setSaveStatus] = useState({});
  const [auditLogs, setAuditLogs] = useState([]);
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState(userProfile?.name || "");
  const [isPersonnelExpanded, setIsPersonnelExpanded] = useState(false);
  
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [reviewTarget, setReviewTarget] = useState(null);

  const [expandedUserIds, setExpandedUserIds] = useState(new Set());

  const showToast = useToast();
  const confirm = useConfirm();

  const isAdmin = role === ROLES.ADMIN;
  const isFirstRun = adminList.length === 0;

  useEffect(() => {
    const timer = setInterval(() => setToday(taipeiDateKey()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (userProfile?.name) setNewName(userProfile.name);
  }, [userProfile]);
  
  useEffect(() => {
    if (!user) return;
    if (isLocalPreview) setCurrentIP("127.0.0.1");
    else fetch("https://api.ipify.org?format=json").then((r) => r.json()).then((d) => setCurrentIP(d.ip)).catch(console.error);
    const unsubSettings = onSnapshot(doc(db, "artifacts", APP_ID, "public", "data", "settings", "main"), (docSnap) => {
        if (docSnap.exists()) { setSettings(docSnap.data()); setAdminList(docSnap.data().adminUIDs || []); }
    });
    
    let unsubUsers = () => {};
    if (isAdmin) {
        const qUsers = query(collection(db, "artifacts", APP_ID, "public", "data", "daily_status"));
        unsubUsers = subscribePersonnel(qUsers,
          uid => doc(db, "artifacts", APP_ID, "users", uid, "profile", "main"),
          (entries) => {
          const pUsers = [];
          const aUsers = [];
          entries.forEach(({ uid, dailyData, profileData: pData }) => {
            const userData = {
              uid,
              name: dailyData.name,
              photoURL: pData.photoURL || null,
              role: pData.role || ROLES.PARTTIME,
              canViewAllHours: pData.canViewAllHours || false, 
              annualLeaveRemaining: pData.annualLeaveRemaining || 0,
              annualLeaveUsed: pData.annualLeaveUsed || 0,
              ...personnelStatusFields(pData, dailyData),
              baseSalary: Number(pData.baseSalary) || 0,
              hourlyRate: Number(pData.hourlyRate) || 0,
              laborInsurance: Number(pData.laborInsurance) || 0,
              healthInsurance: Number(pData.healthInsurance) || 0,
              startDate: pData.startDate || "",
              birthday: pData.birthday || "",
              
              fullName: pData.fullName || "",
              gender: pData.gender || "",
              idNumber: pData.idNumber || "",
              bloodType: pData.bloodType || "",
              address: pData.address || "",
              mobile: pData.mobile || "",
              personalEmail: pData.personalEmail || "",
              education: pData.education || "",
              workExperience: (pData.workExperience && pData.workExperience.length > 0) ? pData.workExperience : [{}, {}], 
              familyMembers: pData.familyMembers || [],
              emergencyContact: pData.emergencyContact || { name: "", relation: "", mobile: "" },
              bankAccount: pData.bankAccount || "",
              
              pendingProfileUpdate: pData.pendingProfileUpdate || null
            };
            if (userData.accountStatus === "pending") pUsers.push(userData);
            else if (["active", "archived"].includes(userData.accountStatus)) aUsers.push(userData);
          });
          setPendingUsers(pUsers);
          setActiveUsers(aUsers);
        }, error => console.error("人員資料同步失敗", error));
    } else {
        getDoc(doc(db, "artifacts", APP_ID, "users", user.uid, "profile", "main")).then(snap => {
            if(snap.exists()) {
                const myData = { uid: user.uid, ...snap.data() };
                setActiveUsers([myData]);
            }
        });
    }

    if (isAdmin) {
      const qAudit = query(collection(db, "artifacts", APP_ID, "public", "data", "audit_logs"), orderBy("timestamp", "desc"), limit(20));
      onSnapshot(qAudit, (snap) => setAuditLogs(snap.docs.map((d) => d.data())));
    }
    if (!isLocalPreview && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((p) => setCurrentLocation({ lat: p.coords.latitude, lng: p.coords.longitude }));
    }
    return () => { unsubSettings(); unsubUsers(); };
  }, [user, isAdmin]);

  const updateSetting = async (key, val) => {
    try { await setDoc(doc(db, "artifacts", APP_ID, "public", "data", "settings", "main"), { [key]: val }, { merge: true }); } catch (e) { alert("設定失敗"); }
  };
  const handleUpdateName = async () => {
    if (!isAdmin || !newName.trim()) return;
    try {
      await updateDoc(doc(db, "artifacts", APP_ID, "users", user.uid, "profile", "main"), { name: newName });
      await updateDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", user.uid), { name: newName });
      setIsEditingName(false); window.location.reload();
    } catch (e) { alert("更新失敗"); }
  };
  const handleUpdateUser = async (targetUid, field, value) => {
    if (field === "role" && activeUsers.find(u => u.uid === targetUid)?.accountStatus === "archived") return;
    try {
      const updateLocalList = (list) => list.map((u) => (u.uid === targetUid ? { ...u, [field]: value } : u));
      setActiveUsers((prev) => updateLocalList(prev));
      setPendingUsers((prev) => updateLocalList(prev));
      if (field === "role") {
        const settingsRef = doc(db, "artifacts", APP_ID, "public", "data", "settings", "main");
        if (value === ROLES.ADMIN) await setDoc(settingsRef, { adminUIDs: arrayUnion(targetUid) }, { merge: true });
        else await setDoc(settingsRef, { adminUIDs: arrayRemove(targetUid) }, { merge: true });
      }
      await updateDoc(doc(db, "artifacts", APP_ID, "users", targetUid, "profile", "main"), { [field]: value });
      // [優化] 若更動到列表頁需要的欄位，順便冗餘同步到 daily_status，減少 N+1 查詢
      if (field === "fullName" || field === "birthday") {
        try {
          await updateDoc(
            doc(db, "artifacts", APP_ID, "public", "data", "daily_status", targetUid),
            { [field]: value }
          );
        } catch (e) { /* daily_status 可能不存在，可忽略 */ }
      }
      setSaveStatus((prev) => ({ ...prev, [targetUid]: Date.now() }));
      setTimeout(() => setSaveStatus((prev) => { const n = { ...prev }; delete n[targetUid]; return n; }), 2000);
    } catch (e) { console.error("更新失敗", e); }
  };
  
  const handleUpdateArrayItem = (uid, arrayName, index, field, value) => {
    const targetUser = activeUsers.find(u => u.uid === uid); if (!targetUser) return;
    const newArray = [...(targetUser[arrayName] || [])];
    if (!newArray[index]) newArray[index] = {}; newArray[index] = { ...newArray[index], [field]: value };
    handleUpdateUser(uid, arrayName, newArray);
  };
  const handleAddArrayItem = (uid, arrayName, initialItem = {}) => {
      const targetUser = activeUsers.find(u => u.uid === uid);
      const newArray = [...(targetUser[arrayName] || []), initialItem];
      handleUpdateUser(uid, arrayName, newArray);
  };
  const handleRemoveArrayItem = (uid, arrayName, index) => {
      const targetUser = activeUsers.find(u => u.uid === uid);
      const newArray = [...(targetUser[arrayName] || [])];
      newArray.splice(index, 1);
      handleUpdateUser(uid, arrayName, newArray);
  };
  const handleUpdateObjectField = (uid, objectName, field, value) => {
      const targetUser = activeUsers.find(u => u.uid === uid);
      const newObj = { ...(targetUser[objectName] || {}), [field]: value };
      handleUpdateUser(uid, objectName, newObj);
  };
  
  const handleToggleViewPermission = async (targetUid, currentValue) => {
    if (activeUsers.find(u => u.uid === targetUid)?.accountStatus === "archived") return;
    try { const newValue = !currentValue; await updateDoc(doc(db, "artifacts", APP_ID, "users", targetUid, "profile", "main"), { canViewAllHours: newValue }); const updateLocalList = (list) => list.map((u) => u.uid === targetUid ? { ...u, canViewAllHours: newValue } : u); setActiveUsers((prev) => updateLocalList(prev)); } catch (e) { alert("權限更新失敗"); }
  };
  const handleToggleHide = async (targetUid, currentHidden) => {
    if (activeUsers.find(u => u.uid === targetUid)?.accountStatus === "archived") return;
    try { const newValue = !currentHidden; await updateDoc(doc(db, "artifacts", APP_ID, "users", targetUid, "profile", "main"), { isHidden: newValue }); await updateDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", targetUid), { isHidden: newValue }); const updateLocalList = (list) => list.map((u) => u.uid === targetUid ? { ...u, isHidden: newValue } : u); setActiveUsers((prev) => updateLocalList(prev)); } catch (e) { alert("操作失敗"); }
  };
  const handleDeleteUser = async (targetUid, targetName) => {
    const ok = await confirm({
      title: "封存使用者",
      message: `確定要封存「${targetName}」嗎？此操作會將帳號標記為已封存。`,
      confirmText: "封存",
      danger: true,
    });
    if (!ok) return;
    try {
      await updateDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", targetUid), { accountStatus: "archived", isHidden: true });
      await updateDoc(doc(db, "artifacts", APP_ID, "users", targetUid, "profile", "main"), { accountStatus: "archived" });
      const settingsRef = doc(db, "artifacts", APP_ID, "public", "data", "settings", "main");
      await setDoc(settingsRef, { adminUIDs: arrayRemove(targetUid) }, { merge: true });
      const archivedUser = activeUsers.find(u => u.uid === targetUid) || pendingUsers.find(u => u.uid === targetUid);
      if (archivedUser) setActiveUsers((prev) => [
        ...prev.filter(u => u.uid !== targetUid),
        { ...archivedUser, accountStatus: "archived", isHidden: true },
      ]);
      setPendingUsers((prev) => prev.filter((u) => u.uid !== targetUid));
      showToast("已封存");
    } catch (e) { alert("操作失敗"); }
  };
  const handleApproveUser = async (targetUid, targetName) => {
    const ok = await confirm({
      title: "核准帳號",
      message: `確定核准「${targetName}」？`,
      confirmText: "核准",
    });
    if (!ok) return;
    try {
      await updateDoc(doc(db, "artifacts", APP_ID, "users", targetUid, "profile", "main"), { accountStatus: "active" });
      await updateDoc(doc(db, "artifacts", APP_ID, "public", "data", "daily_status", targetUid), { accountStatus: "active", isHidden: false });
      showToast("已核准");
      // 移到 active list；無需 reload
      setPendingUsers((prev) => prev.filter((u) => u.uid !== targetUid));
    } catch (e) { alert("失敗"); }
  };
  
  const toggleUserDetail = (uid) => {
      const newSet = new Set(expandedUserIds);
      if(newSet.has(uid)) newSet.delete(uid);
      else newSet.add(uid);
      setExpandedUserIds(newSet);
  };

  const claimAdmin = async () => { await setDoc(doc(db, "artifacts", APP_ID, "public", "data", "settings", "main"), { adminUIDs: arrayUnion(user.uid) }, { merge: true }); window.location.reload(); };
  const getRoleBadgeColor = (r) => { if (r === ROLES.ADMIN) return "bg-purple-100 text-purple-700 border-purple-200"; if (r === ROLES.FULLTIME) return "bg-blue-100 text-blue-700 border-blue-200"; return "bg-slate-100 text-slate-600 border-slate-200"; };

  // [新增] 根據 actionType 取得樣式的 helper function
  const getLogStyle = (type) => {
      if (!type) return "bg-slate-50 border-slate-100 text-slate-600";
      if (type.includes("刪除") || type.includes("Delete")) return "bg-red-50 border-red-100 text-red-700";
      if (type.includes("補打卡") || type.includes("Add")) return "bg-green-50 border-green-100 text-green-700";
      if (type.includes("修改") || type.includes("Edit")) return "bg-blue-50 border-blue-100 text-blue-700";
      return "bg-slate-50 border-slate-100 text-slate-600";
  };

  if (!userProfile) return <div className="p-8 text-center text-slate-400">載入設定中...</div>;

  // 拆分在職 / 離職（隱藏）人員
  const visibleActive = activeUsers.filter((u) => !isFormerPersonnel(u, today));
  const hiddenActive = activeUsers.filter((u) => isFormerPersonnel(u, today));

  const renderUserCard = (u) => {
    const isExpanded = expandedUserIds.has(u.uid);
    const isArchived = u.accountStatus === "archived";
    const isFormer = isFormerPersonnel(u, today);
    return (
    <div key={u.uid} data-testid={`personnel-${u.uid}`} className={`relative p-4 rounded-xl border transition-all ${isFormer ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200 shadow-sm"}`}>
    <div className="flex justify-between items-start mb-3">
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">{u.photoURL ? <img src={u.photoURL} className="w-full h-full object-cover" referrerPolicy="no-referrer"/> : <User className="p-2 w-full h-full text-slate-400" />}</div>
            <div>
                <div className="font-bold text-slate-800 text-sm flex items-center gap-2">
                    {u.name}
                    {saveStatus[u.uid] && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded border border-green-100 flex items-center gap-1"><Check size={10} /> 已儲存</span>}
                </div>
                <div className={`text-[10px] px-1.5 py-0.5 rounded border inline-block mt-1 ${getRoleBadgeColor(u.role)}`}>{u.role === ROLES.ADMIN ? "Admin" : u.role === ROLES.FULLTIME ? "Fulltime" : "Parttime"}</div>
                {isFormer && <span className="text-xs text-slate-500 ml-2">{isArchived ? "已封存" : u.endDate && u.endDate < today ? "已離職" : "已隱藏"}</span>}
            </div>
        </div>

        {/* [管理員動作] */}
        {u.uid !== user.uid && !isArchived && (
            <div className="flex gap-2">
                {/* 審核資料變更按鈕 */}
                {u.pendingProfileUpdate && (
                    <button onClick={() => setReviewTarget(u)} className="px-2 py-1 bg-orange-100 text-orange-600 rounded text-xs font-bold flex items-center gap-1 animate-pulse">
                        <AlertCircle size={12}/> 審核變更
                    </button>
                )}
                <button onClick={() => handleToggleViewPermission(u.uid, u.canViewAllHours)} className={`p-1.5 rounded-lg transition-colors ${u.canViewAllHours ? "bg-blue-100 text-blue-600" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`} title={u.canViewAllHours ? "已授權查看工時" : "無查看工時權限"}><FileText size={16} /></button>
                <button onClick={() => handleToggleHide(u.uid, u.isHidden)} className={`p-1.5 rounded-lg transition-colors ${u.isHidden ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`} title={u.isHidden ? "顯示" : "隱藏"}>{u.isHidden ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                <button onClick={() => handleDeleteUser(u.uid, u.name)} className="p-1.5 bg-red-50 text-red-500 rounded-lg hover:bg-red-100 transition-colors" title="封存"><Trash2 size={16} /></button>
            </div>
        )}
    </div>

    {/* 角色與權限 (預設顯示) */}
    <div className="grid grid-cols-2 gap-3 mb-3">
        <div><label className="text-[10px] text-slate-400 block mb-1">角色權限</label><select disabled={isArchived} className="w-full border rounded-lg text-xs p-2 bg-slate-50 outline-none focus:ring-2 focus:ring-blue-100" value={u.role} onChange={(e) => handleUpdateUser(u.uid, "role", e.target.value)}><option value={ROLES.PARTTIME}>工讀生</option><option value={ROLES.FULLTIME}>正職</option><option value={ROLES.ADMIN}>管理員</option></select></div>
        {(u.role === ROLES.FULLTIME || u.role === ROLES.ADMIN) && (<div><label className="text-[10px] text-slate-400 block mb-1">特休 (剩/用)</label><div className="flex items-center border border-slate-200 rounded-lg bg-slate-50 overflow-hidden"><input type="number" className="flex-1 min-w-0 text-xs p-2 bg-white outline-none font-bold text-center text-blue-600 focus:bg-blue-50" defaultValue={u.annualLeaveRemaining} onBlur={(e) => handleUpdateUser(u.uid, "annualLeaveRemaining", parseInt(e.target.value))}/><div className="px-2 py-2 text-[10px] text-slate-500 bg-slate-100 border-l border-slate-200 whitespace-nowrap">用 {u.annualLeaveUsed}</div></div></div>)}
    </div>

    {/* 薪資與保險 ([修改] 移到這裡預設顯示) */}
    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 mb-3">
        <div className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1"><DollarSign size={12} /> 薪資與保險設定</div>
        <div className="grid grid-cols-2 gap-3">
        <div><label className="text-[10px] text-slate-400 block mb-1">{u.role === ROLES.FULLTIME ? "底薪" : "時薪"}</label><input type="number" className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" defaultValue={u.role === ROLES.FULLTIME ? u.baseSalary : u.hourlyRate} onBlur={(e) => handleUpdateUser(u.uid, u.role === ROLES.FULLTIME ? "baseSalary" : "hourlyRate", Number(e.target.value))}/></div>
        <div><label className="text-[10px] text-slate-400 block mb-1">到職日</label><input type="date" className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" defaultValue={u.startDate} onBlur={(e) => handleUpdateUser(u.uid, "startDate", e.target.value)}/></div>
        <div className="col-span-2"><label className="text-[10px] text-slate-400 block mb-1">離職日 <span className="text-slate-300">(未離職請留空；到職月與離職月勞保按加保天數比例計算)</span></label><input aria-label="離職日" type="date" className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" value={u.endDate || ""} onChange={(e) => handleUpdateUser(u.uid, "endDate", e.target.value)}/></div>

        {/* 銀行帳號欄位 */}
        <div className="col-span-2 border-t border-slate-200 pt-2 mt-1">
            <label className="text-[10px] text-slate-400 block mb-1">銀行帳號 (國泰世華 013)</label>
            <div className="flex gap-2">
                <div className="bg-white border rounded px-2 py-1.5 text-xs text-slate-500 min-w-[100px] flex items-center justify-center">戶名: {u.fullName || "--"}</div>
                <input className="flex-1 border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" placeholder="輸入帳號" value={u.bankAccount || ""} onChange={(e) => handleUpdateUser(u.uid, "bankAccount", e.target.value)}/>
            </div>
        </div>

        <div><label className="text-[10px] text-slate-400 block mb-1">勞保自付</label><input type="number" className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" defaultValue={u.laborInsurance} onBlur={(e) => handleUpdateUser(u.uid, "laborInsurance", Number(e.target.value))}/></div>
        <div><label className="text-[10px] text-slate-400 block mb-1">健保自付</label><input type="number" className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" defaultValue={u.healthInsurance} onBlur={(e) => handleUpdateUser(u.uid, "healthInsurance", Number(e.target.value))}/></div>
        </div>
    </div>

    {/* [修改] 展開/收起詳細資料按鈕 */}
    <button
        onClick={() => toggleUserDetail(u.uid)}
        className="w-full py-2 text-xs font-bold text-slate-500 bg-slate-50 hover:bg-slate-100 rounded-lg flex items-center justify-center gap-1 transition-colors border border-dashed border-slate-300 mb-2"
    >
        {isExpanded ? (
            <><ChevronUp size={14}/> 收起詳細人事資料</>
        ) : (
            <><ChevronDown size={14}/> 查看/編輯詳細人事資料</>
        )}
    </button>

    {/* [修改] 隱藏的詳細資料區塊 */}
    {isExpanded && (
        <div className="animate-fade-in space-y-3 pt-2">
            {/* 基本人事資料 */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1"><FileBadge size={12} /> 基本人事資料</div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="col-span-1"><label className="text-[10px] text-slate-400 block mb-1">中文全名</label><input className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" placeholder="例：王小明" value={u.fullName || ""} onChange={(e) => handleUpdateUser(u.uid, "fullName", e.target.value)}/></div>
                    <div className="col-span-1"><label className="text-[10px] text-slate-400 block mb-1">性別</label><select className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100 bg-white" value={u.gender || ""} onChange={(e) => handleUpdateUser(u.uid, "gender", e.target.value)}><option value="">請選擇</option><option value="male">男</option><option value="female">女</option><option value="other">其他</option></select></div>
                    <div className="col-span-1"><label className="text-[10px] text-slate-400 block mb-1">身份證字號</label><input className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" value={u.idNumber || ""} onChange={(e) => handleUpdateUser(u.uid, "idNumber", e.target.value)}/></div>
                    <div className="col-span-1"><label className="text-[10px] text-slate-400 block mb-1">血型</label><select className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100 bg-white" value={u.bloodType || ""} onChange={(e) => handleUpdateUser(u.uid, "bloodType", e.target.value)}><option value="">未知</option><option value="A">A型</option><option value="B">B型</option><option value="O">O型</option><option value="AB">AB型</option></select></div>
                    <div className="col-span-1"><label className="text-[10px] text-slate-400 block mb-1">出生年月日（民國）</label><RocDateInput className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" value={u.birthday || ""} onChange={(value) => handleUpdateUser(u.uid, "birthday", value)}/></div>
                    <div className="col-span-1"><label className="text-[10px] text-slate-400 block mb-1">手機號碼</label><input className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" value={u.mobile || ""} onChange={(e) => handleUpdateUser(u.uid, "mobile", e.target.value)}/></div>
                    <div className="col-span-2"><label className="text-[10px] text-slate-400 block mb-1">通訊地址</label><input className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" value={u.address || ""} onChange={(e) => handleUpdateUser(u.uid, "address", e.target.value)}/></div>
                    <div className="col-span-2"><label className="text-[10px] text-slate-400 block mb-1">電子信箱 (Email)</label><input className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" value={u.personalEmail || ""} onChange={(e) => handleUpdateUser(u.uid, "personalEmail", e.target.value)}/></div>
                    <div className="col-span-2"><label className="text-[10px] text-slate-400 block mb-1">畢業學校及科系</label><input className="w-full border rounded-lg text-xs p-2 outline-none focus:ring-2 focus:ring-blue-100" placeholder="例：台灣大學 資訊工程系" value={u.education || ""} onChange={(e) => handleUpdateUser(u.uid, "education", e.target.value)}/></div>
                </div>
            </div>

            {/* 工作經歷 */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-xs font-bold text-slate-500 flex items-center gap-1"><Briefcase size={12} /> 工作經歷</div>
                    <button onClick={()=>handleAddArrayItem(u.uid, "workExperience")} className="text-[10px] text-blue-500 flex items-center gap-1 hover:underline"><Plus size={10}/> 新增</button>
                </div>
                <div className="space-y-2">
                    {u.workExperience?.map((work, idx) => (
                        <div key={idx} className="mb-2 p-2 bg-white border border-slate-200 rounded relative">
                            <button onClick={()=>handleRemoveArrayItem(u.uid, "workExperience", idx)} className="absolute top-2 right-2 text-slate-400 hover:text-red-500"><Trash2 size={12}/></button>
                            <div className="space-y-2 pr-6">
                                <input className="w-full border rounded text-xs p-1.5" placeholder="工作機構" value={work.company || ""} onChange={e => handleUpdateArrayItem(u.uid, "workExperience", idx, "company", e.target.value)}/>
                                <div className="flex gap-2">
                                    <input className="w-full border rounded text-xs p-1.5" placeholder="職別" value={work.job || ""} onChange={e => handleUpdateArrayItem(u.uid, "workExperience", idx, "job", e.target.value)}/>
                                    <input className="w-full border rounded text-xs p-1.5" placeholder="任職期間" value={work.period || ""} onChange={e => handleUpdateArrayItem(u.uid, "workExperience", idx, "period", e.target.value)}/>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 家庭與緊急聯絡 */}
            <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <div className="flex justify-between items-center mb-2">
                    <div className="text-xs font-bold text-slate-500 flex items-center gap-1"><Heart size={12} /> 家庭成員</div>
                    <button onClick={()=>handleAddArrayItem(u.uid, "familyMembers")} className="text-[10px] text-blue-500 flex items-center gap-1 hover:underline"><Plus size={10}/> 新增</button>
                </div>
                <div className="space-y-2 mb-4">
                    {u.familyMembers?.map((fam, idx) => (
                        <div key={idx} className="flex gap-2 items-center">
                            <input className="w-1/3 border rounded text-xs p-1.5" placeholder="姓名" value={fam.name || ""} onChange={e => handleUpdateArrayItem(u.uid, "familyMembers", idx, "name", e.target.value)}/>
                            <input className="flex-1 border rounded text-xs p-1.5" placeholder="稱謂" value={fam.title || ""} onChange={e => handleUpdateArrayItem(u.uid, "familyMembers", idx, "title", e.target.value)}/>
                            <button onClick={()=>handleRemoveArrayItem(u.uid, "familyMembers", idx)} className="text-slate-400 hover:text-red-500"><Trash2 size={12}/></button>
                        </div>
                    ))}
                </div>
                <div className="pt-2 border-t border-slate-200">
                    <div className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1"><PhoneCall size={12} /> 緊急聯絡人</div>
                    <div className="flex gap-2">
                        <input className="w-1/4 border rounded text-xs p-1.5" placeholder="姓名" value={u.emergencyContact?.name || ""} onChange={e => handleUpdateObjectField(u.uid, "emergencyContact", "name", e.target.value)}/>
                        <input className="w-1/4 border rounded text-xs p-1.5" placeholder="關係" value={u.emergencyContact?.relation || ""} onChange={e => handleUpdateObjectField(u.uid, "emergencyContact", "relation", e.target.value)}/>
                        <input className="flex-1 border rounded text-xs p-1.5" placeholder="手機號碼" value={u.emergencyContact?.mobile || ""} onChange={e => handleUpdateObjectField(u.uid, "emergencyContact", "mobile", e.target.value)}/>
                    </div>
                </div>
            </div>
        </div>
    )}
    </div>
    );
  };

  return (
    <div className="paper-page settings-view p-4 pb-24">
      {showEditProfile && <EditProfileModal user={user} userData={activeUsers.find(u=>u.uid === user.uid) || userProfile} canEdit={isAdmin} onClose={()=>setShowEditProfile(false)} onSubmitted={()=>showToast("變更已送出，等待管理員審核")} />}
      {reviewTarget && <ReviewModal targetUser={reviewTarget} onClose={()=>setReviewTarget(null)} confirm={confirm} showToast={showToast} />}

      <PageHeading icon={Settings} charm="pinecone">系統設定</PageHeading>

      {/* 個人資料卡片 */}
      <div className="paper-item profile-summary bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6 flex justify-between items-center relative overflow-hidden">
        <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
        <div className="flex items-center gap-4 flex-1">
          <div className="h-14 w-14 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden shadow-inner">
            {userProfile.photoURL ? (
              <img
                src={userProfile.photoURL}
                alt="Me"
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            ) : (
              <User size={24} className="text-slate-400" />
            )}
          </div>
          <div className="flex-1">
            {isAdmin && isEditingName ? (
              <div className="flex gap-2 items-center">
                <input
                  className="border-b-2 border-blue-500 bg-transparent px-1 text-lg font-bold w-32 focus:outline-none"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
                <button onClick={handleUpdateName} className="text-green-600 bg-green-50 p-1 rounded hover:bg-green-100"><Check size={16} /></button>
                <button onClick={() => setIsEditingName(false)} className="text-red-500 bg-red-50 p-1 rounded hover:bg-red-100"><X size={16} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-2 group">
                <div className="font-bold text-slate-800 text-lg">{userProfile.name}</div>
                {isAdmin && <button title="修改顯示名稱" aria-label="修改顯示名稱" onClick={() => setIsEditingName(true)}><Edit2 size={14} className="text-slate-300 group-hover:text-blue-500 transition-colors" /></button>}
              </div>
            )}
            <div className="text-xs text-slate-400 mt-1">{user.email}</div>
            <div className="flex gap-2 items-center mt-2">
                <span className={`text-[10px] px-2 py-0.5 rounded border inline-block ${getRoleBadgeColor(role)}`}>
                    {role === ROLES.ADMIN ? "超級管理員" : role === ROLES.FULLTIME ? "正職人員" : "工讀生"}
                </span>
                {/* 編輯詳細資料按鈕 */}
                <button onClick={() => setShowEditProfile(true)} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-200 flex items-center gap-1 transition-colors">
                    <FileText size={10}/> {isAdmin ? "編輯詳細資料" : "查看詳細資料"}
                </button>
            </div>
            {/* 提示正在等待審核 */}
            {activeUsers.find(u=>u.uid===user.uid)?.pendingProfileUpdate && (
                <div className="mt-1 text-[10px] text-orange-500 flex items-center gap-1">
                    <AlertCircle size={10}/> 資料變更審核中
                </div>
            )}
          </div>
        </div>
        <button
          onClick={async () => {
            const ok = await confirm({
              title: "登出",
              message: "確定要登出嗎？",
              confirmText: "登出",
            });
            if (ok) signOut(auth);
          }}
          className="bg-slate-100 text-slate-500 hover:bg-red-50 hover:text-red-600 px-4 py-2 rounded-lg text-sm font-bold transition-all"
        >
          登出
        </button>
      </div>

      {isFirstRun ? (
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white p-6 rounded-2xl shadow-lg text-center mb-6">
          <Shield className="w-12 h-12 mx-auto mb-2 opacity-80" />
          <h3 className="font-bold text-lg">系統初始化</h3>
          <p className="text-sm opacity-90 mb-4">目前無管理員，請設定自己為超級管理員。</p>
          <button onClick={claimAdmin} className="bg-white text-blue-600 px-6 py-2 rounded-full font-bold shadow-lg hover:shadow-xl transition-all">成為管理員</button>
        </div>
      ) : isAdmin ? (
        <>
          {/* 打卡位置設定 */}
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
              <MapPin size={18} className="text-blue-500" /> 打卡位置與 IP 設定
            </h3>
            <div className="space-y-4">
                <div>
                    <h4 className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1"><Navigation size={12}/> GPS 定位</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-100 p-3 rounded-xl border border-slate-200">
                            <div className="text-[10px] text-slate-400 mb-1">系統目前設定</div>
                            <div className="font-mono font-bold text-slate-600 text-sm truncate">{settings.companyLat ? `${settings.companyLat.toFixed(5)}, ${settings.companyLng.toFixed(5)}` : "尚未設定"}</div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 flex flex-col justify-between">
                            <div>
                                <div className="text-[10px] text-blue-400 mb-1">您目前的位置</div>
                                <div className="font-mono font-bold text-blue-700 text-sm truncate">{currentLocation ? `${currentLocation.lat.toFixed(5)}, ${currentLocation.lng.toFixed(5)}` : "定位中..."}</div>
                            </div>
                            <button onClick={() => updateSetting("companyLat", currentLocation?.lat) || updateSetting("companyLng", currentLocation?.lng)} className="mt-2 text-xs bg-blue-600 text-white w-full py-1.5 rounded-lg font-bold hover:bg-blue-700 shadow-sm">更新為此位置</button>
                        </div>
                    </div>
                </div>
                <div>
                    <h4 className="text-xs font-bold text-slate-500 mb-2 flex items-center gap-1"><Globe size={12}/> 網路 IP</h4>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-100 p-3 rounded-xl border border-slate-200">
                            <div className="text-[10px] text-slate-400 mb-1">系統目前設定</div>
                            <div className="font-mono font-bold text-slate-600 text-sm truncate">{settings.allowedIP || "尚未設定"}</div>
                        </div>
                        <div className="bg-blue-50 p-3 rounded-xl border border-blue-100 flex flex-col justify-between">
                            <div>
                                <div className="text-[10px] text-blue-400 mb-1">您目前的 IP</div>
                                <div className="font-mono font-bold text-blue-700 text-sm truncate">{currentIP}</div>
                            </div>
                            <button onClick={() => updateSetting("allowedIP", currentIP)} className="mt-2 text-xs bg-blue-600 text-white w-full py-1.5 rounded-lg font-bold hover:bg-blue-700 shadow-sm">更新為此 IP</button>
                        </div>
                    </div>
                </div>
            </div>
          </div>

          {/* 系統操作紀錄 */}
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
            <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
              <ClipboardList size={18} className="text-blue-500" /> 系統操作紀錄
            </h3>
            <div className="space-y-2 max-h-60 overflow-y-auto pr-1 scrollbar-thin">
              {auditLogs.length === 0 ? <div className="text-center text-slate-400 text-sm py-4">無操作紀錄</div> : auditLogs.map((log, idx) => {
                  const styleClass = getLogStyle(log.actionType);
                  return (
                    <div key={idx} className={`text-xs border rounded-lg p-2.5 mb-2 ${styleClass}`}>
                        <div className="flex justify-between items-center mb-1">
                            <span className="font-bold px-1.5 py-0.5 rounded bg-white/50">{log.actionType || "操作"}</span>
                            <span className="opacity-70">{log.timestamp?.toDate().toLocaleString()}</span>
                        </div>
                        <div className="opacity-90">{log.description || `操作者: ${log.operatorName || "未知"}`}</div>
                    </div>
                  );
              })}
            </div>
          </div>

          {/* 人員管理 (最底層 + 可收折) */}
          <div className="bg-white p-5 rounded-2xl shadow-sm border border-slate-100 mb-6">
            <div className="flex justify-between items-center cursor-pointer select-none mb-2" onClick={() => setIsPersonnelExpanded(!isPersonnelExpanded)}>
                <h3 className="font-bold text-slate-700 flex items-center gap-2"><User size={18} className="text-blue-500" /> 人員管理</h3>
                {isPersonnelExpanded ? <ChevronUp size={20} className="text-slate-400"/> : <ChevronDown size={20} className="text-slate-400"/>}
            </div>

            {isPersonnelExpanded && (
                <div className="mt-4 animate-fade-in">
                    {/* 測試工具（薪資年資功能） */}
                    <TestDataPanel showToast={showToast} confirm={confirm} />

                    {/* 待審核用戶 */}
                    {pendingUsers.length > 0 && (
                    <div className="mb-6 border-2 border-orange-200 bg-orange-50 rounded-xl p-4">
                        <h4 className="text-orange-700 font-bold mb-3 flex items-center gap-2"><UserPlus size={16} /> 待審核人員 ({pendingUsers.length})</h4>
                        <div className="space-y-3">
                        {pendingUsers.map((u) => (
                            <div key={u.uid} className="bg-white p-3 rounded-lg border border-orange-200 flex justify-between items-center shadow-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-100 overflow-hidden">{u.photoURL ? <img src={u.photoURL} className="w-full h-full object-cover" referrerPolicy="no-referrer"/> : <User className="p-1 w-full h-full text-slate-400" />}</div>
                                <span className="font-bold text-slate-800 text-sm">{u.name}</span>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={() => handleDeleteUser(u.uid, u.name)} className="text-red-400 p-2 hover:bg-red-50 rounded text-xs font-bold">拒絕</button>
                                <button onClick={() => handleApproveUser(u.uid, u.name)} className="bg-orange-500 text-white px-4 py-1.5 rounded-lg text-xs font-bold hover:bg-orange-600 shadow-sm">核准</button>
                            </div>
                            </div>
                        ))}
                        </div>
                    </div>
                    )}

                    {/* 在職人員 */}
                    <div className="space-y-4">
                      <h4 className="text-slate-500 font-bold text-xs mb-2">在職人員 ({visibleActive.length})</h4>
                      {visibleActive.map(renderUserCard)}
                    </div>

                    {/* 離職／隱藏人員 */}
                    {hiddenActive.length > 0 && (
                      <div className="space-y-4 mt-8 pt-6 border-t-2 border-dashed border-slate-200">
                        <h4 className="text-slate-400 font-bold text-xs mb-2 flex items-center gap-2">
                          <EyeOff size={14}/> 離職／已隱藏人員 ({hiddenActive.length})
                        </h4>
                        {hiddenActive.map(renderUserCard)}
                      </div>
                    )}
                </div>
            )}
          </div>
        </>
      ) : (
        <div className="text-center text-slate-400 py-10 flex flex-col items-center">
          <AlertCircle size={40} className="mb-2 text-slate-200" />
          <span className="text-sm">無管理員權限</span>
        </div>
      )}
    </div>
  );
}
